import React from "react";
import InputTagList from "../../components/input-tag-list.jsx";
import ProviderFilter from "../../components/content-list/filters/provider-filter.jsx";
import ContentTypeFilter from "../../components/content-list/filters/content-type-filter.jsx";
import DurationFilter from "../../components/content-list/filters/duration-filter.jsx";
import SeasonFilter from "../../components/content-list/filters/season-filter.jsx";
import SeriesFilter from "../../components/content-list/filters/series-filter.jsx";
import RatingFilter from "../../components/content-list/filters/rating-filter.jsx";
import GenreFilter, { SubgenreFilter } from "../../components/content-list/filters/genre-filter.jsx";
import getTagsFromFilters from "../../components/content-list/getTagsFromFilters.jsx";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import KeywordFilter from "../../components/content-list/filters/keyword-filter.jsx";
import axios, { errorHandler, responseHandler } from "../../requests/axios.js";
import { toast } from "react-toastify";
import { filtersToDefinitions } from "./helpers/to-template-item.js";
import LoopRoundedIcon from "@mui/icons-material/LoopRounded";
import Loader from "../../common/loader.jsx";
import { formatDuration, intervalToDuration } from "date-fns";

const contentTypes = [
  {
    value: "features",
    label: "Features",
  },
  {
    value: "episodes",
    label: "Episodes",
  },
  {
    value: "promos",
    label: "Promos",
  },
  {
    value: "series",
    label: "Series",
  },
];
const defaultContentType = contentTypes[0];

function TemplateBlockFilters({ filters, updateFilters, providers, changeBlockTypeToFilter, forChannelId }) {
  const [filterStrength, setFilterStrength] = React.useState("00:00:00:00");
  const [isLoading, setLoading] = React.useState(false);
  const tagsMemo = React.useMemo(() => getTagsFromFilters(filters), [filters]);
  const initialState = {
    search: "",
    providers: [{ value: providers[0].provider_guid, label: providers[0].provider_name }],
    contentType: [defaultContentType],
    ratings: [],
    keywords: [],
    genres: [],
    subgenres: [],
    durationMin: "",
    durationMax: "",
    series: {},
    season: {},
    forChannelId: forChannelId,
  };

  // Filter setters
  function setSearch(input) {
    updateFilters((prev) => ({
      ...prev,
      search: input,
    }));
  }

  const setProvider = React.useCallback(
    (input) => {
      updateFilters((prev) => ({
        ...prev,
        providers: !input.length ? prev.providers : input,
      }));
    },
    [updateFilters],
  );

  function setContentType(input) {
    updateFilters((prev) => ({
      ...prev,
      contentType: !input.length ? prev.contentType : input,
      series: input.length && input[0].value === "episodes" ? prev.series : {},
      season: input.length && input[0].value === "episodes" ? prev.season : {},
    }));
  }

  function setRatings(rating) {
    updateFilters((prev) => {
      const ratings = prev.ratings
        .concat(rating)
        .filter((value, index, self) => index === self.findIndex((t) => t.value === value.value && t.label));
      return {
        ...prev,
        ratings,
      };
    });
  }

  function setKeywords(keyword) {
    updateFilters((prev) => {
      const keywords = prev.keywords
        .concat(keyword)
        .filter((value, index, self) => index === self.findIndex((k) => k.value === value.value && k.label));
      return {
        ...prev,
        keywords,
      };
    });
  }

  function setGenre(genre) {
    let subgenres = [];
    let existing = filters.genres.length ? filters.genres[0].value : null;
    if (+genre.value === +existing) {
      subgenres = filters.subgenres;
    }

    updateFilters((prev) => ({
      ...prev,
      genres: [genre],
      subgenres,
    }));
  }

  function setSubgenre(subgenres) {
    let nextSubgenres = subgenres;
    if (!nextSubgenres) {
      nextSubgenres = [];
    }

    updateFilters((prev) => ({
      ...prev,
      subgenres: nextSubgenres,
    }));
  }

  function setDurationMin(event) {
    const durationValue = Math.min(Math.max(0, event.target.value), 999);
    event.target.value = durationValue; // for visibility of changes to value outside range
    updateFilters((prev) => ({
      ...prev,
      durationMin: durationValue ? durationValue : "",
    }));
  }

  function setDurationMax(event) {
    const durationValue = Math.min(Math.max(0, event.target.value), 999);
    event.target.value = durationValue; // for visibility of changes to value outside range
    updateFilters((prev) => ({
      ...prev,
      durationMax: durationValue ? durationValue : "",
    }));
  }

  function setSeries(input) {
    updateFilters((prev) => {
      let season = {};
      if (input.value === prev.series?.value) {
        season = prev.season;
      }

      return {
        ...prev,
        series: input,
        season,
      };
    });
  }

  function setSeason(input) {
    updateFilters((prev) => ({
      ...prev,
      season: input,
    }));
  }

  function onRemoveTag(tag) {
    const keys = Array.isArray(tag) ? tag : [tag];
    const keyOptions = keys.map((tagKey) => {
      const [key, option] = tagKey.split(".");
      return {
        key,
        option,
      };
    });

    updateFilters((prev) => {
      const next = keyOptions
        .map((keyOption) => ({
          key: [keyOption.key],
          value: keyOption.option
            ? prev[keyOption.key].filter((v) => v.value !== keyOption.option)
            : initialState[keyOption.key],
        }))
        .reduce((previousValue, currentValue) => {
          return {
            ...previousValue,
            [currentValue.key]: currentValue.value,
          };
        }, {});

      // clear subgenres
      if (next.genres) {
        next.subgenres = [];
      }

      return {
        ...prev,
        ...next,
      };
    });
  }

  function checkFilterStrength() {
    const definitions = filtersToDefinitions(filters);
    setLoading(true);

    axios
      .post(`api/templates/filter-strength`, { definitions, channel_id: forChannelId })
      .then(
        responseHandler((resp) => {
          setFilterStrength(
            resp.data !== 0 ? formatDuration(intervalToDuration({ start: 0, end: resp.data * 1000 })) : "None",
          );
        }),
      )
      .catch(
        errorHandler((e) => {
          console.error(e);
          toast.error("Error fetching filter strength.");
        }),
      )
      .finally(() => setLoading(false));
  }

  return (
    <div className="content-list__filter content-list-filter" onMouseDown={changeBlockTypeToFilter}>
      <div className="content-list-filter__group content-list-filter__group--fixed content-list-filter__group--left content-list-filter__group--align-center">
        {isLoading ? (
          <Loader height={45} width={45} />
        ) : (
          <React.Fragment>
            <button className="btn btn--icon" onClick={checkFilterStrength}>
              <LoopRoundedIcon />
            </button>
            Content Duration Match: {filterStrength}
          </React.Fragment>
        )}
      </div>

      <InputTagList tags={tagsMemo} onRemove={onRemoveTag} />

      <div className="content-list-filter__group content-list-filter__group--fixed">
        <div className="input-group input-group--with-icon content-list-filter__search">
          <input
            type="text"
            className="app-text-input"
            placeholder="Search..."
            onChange={(e) => setSearch(e.target.value)}
            value={filters.search}
          />
          <SearchRoundedIcon />
        </div>
      </div>
      <div className="content-list-filter__group">
        <div className="content-list-filter__provider">
          <ProviderFilter
            providers={providers}
            selectedProviders={filters.providers}
            onChange={setProvider}
            multiSelect={true}
          />
        </div>
        <div className="content-list-filter__content-type">
          <ContentTypeFilter
            contentType={filters.contentType}
            contentTypes={contentTypes}
            onChange={setContentType}
            multiSelect={true}
          />
        </div>
        <div className="content-list-filter__ratings content-list-filter__ratings--thirds">
          <RatingFilter selectedRatings={filters.ratings} onChange={setRatings} />
        </div>
        <div className="content-list-filter__genres content-list-filter__genres--thirds">
          <KeywordFilter selectedKeywords={filters.keywords} onChange={setKeywords} />
        </div>
        <div className="content-list-filter__ratings content-list-filter__ratings--thirds">
          <GenreFilter selectedGenres={filters.genres} onChange={setGenre} />
        </div>
        <div className="content-list-filter__ratings">
          <SubgenreFilter
            selectedGenres={filters.genres}
            selectedSubgenres={filters.subgenres}
            onChange={setSubgenre}
            multiSelect={true}
          />
        </div>
        <div className="content-list-filter__duration">
          <DurationFilter
            onChangeMax={setDurationMax}
            onChangeMin={setDurationMin}
            min={filters.durationMin}
            max={filters.durationMax}
          />
        </div>
        {filters.contentType.length === 1 && filters.contentType[0].value === "episodes" ? (
          <div className="content-list-filter__series">
            <SeriesFilter
              channelId={forChannelId}
              providerGuids={filters.providers.map((p) => p.value)}
              selectedSeries={filters.series}
              onChange={setSeries}
            />
          </div>
        ) : null}
        {filters.contentType.length === 1 && filters.contentType[0].value === "episodes" ? (
          <div className="content-list-filter__seasons">
            <SeasonFilter seriesId={filters.series.value} season={filters.season} onChange={setSeason} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default TemplateBlockFilters;
